import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { error } = useNotification();
  
  const [stats, setStats] = useState({
    totalKeys: 0,
    availableKeys: 0,
    usedKeys: 0,
    totalProducts: 0
  });
  
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [keysResponse, statsResponse] = await Promise.all([
        axios.get('/api/users/my-keys'),
        axios.get('/api/users/stats')
      ]);
      
      // Extract the keys array from the response object
      const keys = keysResponse.data.keys || [];
      
      // Use the stats from the stats endpoint
      setUserStats(statsResponse.data || {});
      
      // Update the stats based on the keys data
      setStats({
        totalKeys: keys.length,
        availableKeys: keys.filter(key => key.status === 'available').length,
        usedKeys: keys.filter(key => key.status === 'used').length,
        totalProducts: (statsResponse.data && statsResponse.data.totalProducts) || 0
      });
      
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      error('Could not load your dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = 'purple' }) => (
    <div className={`card-astral p-6 hover:glow-${color} transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className={`text-3xl opacity-80`}>{icon}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="card-astral p-6">
        <h1 className="text-3xl font-bold text-white mb-2">
          Welcome back, {user?.username}! 👋
        </h1>
        <p className="text-gray-400">
          {user?.role === 'user' && 'Ready to purchase some license keys?'}
          {user?.role === 'reseller' && 'Time to generate some keys and earn credits!'}
          {user?.role === 'owner' && 'Manage your platform and oversee operations.'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Keys"
          value={stats.totalKeys}
          icon="🔑"
          color="purple"
        />
        
        {user?.role !== 'user' && (
          <StatCard
            title="Available Keys"
            value={stats.availableKeys}
            icon="✅"
            color="blue"
          />
        )}
        
        <StatCard
          title={user?.role === 'user' ? 'Purchased Keys' : 'Used Keys'}
          value={stats.usedKeys}
          icon="🏆"
          color="purple"
        />
        
        <StatCard
          title="Total Products"
          value={stats.totalProducts}
          icon="📦"
          color="blue"
        />
      </div>

      {/* Role-specific quick actions */}
      <div className="card-astral p-6">
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {user?.role === 'user' && (
            <>
              <button
                onClick={() => navigate('/products')}
                className="btn-neon-blue p-4 text-left"
              >
                <div className="text-lg font-semibold">Browse Products</div>
                <div className="text-sm opacity-80">Find licenses to purchase</div>
              </button>
              <button
                onClick={() => navigate('/my-keys')}
                className="btn-neon p-4 text-left"
              >
                <div className="text-lg font-semibold">View My Keys</div>
                <div className="text-sm opacity-80">Check purchased licenses</div>
              </button>
              <button
                onClick={() => navigate('/add-balance')}
                className="btn-neon-blue p-4 text-left"
              >
                <div className="text-lg font-semibold">Add Balance</div>
                <div className="text-sm opacity-80">Top up your account</div>
              </button>
            </>
          )}
          
          {user?.role === 'reseller' && (
            <>
              <button
                onClick={() => navigate('/my-keys')}
                className="card-astral p-4 hover:glow-blue transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">🔑</span>
                  <span className="text-xs text-blue-400 group-hover:text-blue-300">
                    {stats.totalKeys} keys
                  </span>
                </div>
                <div className="font-semibold text-white mb-1">My Generated Keys</div>
                <div className="text-sm opacity-80 text-gray-300">
                  View and manage your generated licenses
                </div>
              </button>

              <button
                onClick={() => navigate('/admin')}
                className="card-astral p-4 hover:glow-green transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">💰</span>
                  <span className="text-xs text-green-400 group-hover:text-green-300">
                    Direct Purchase
                  </span>
                </div>
                <div className="font-semibold text-white mb-1">Reseller Panel</div>
                <div className="text-sm opacity-80 text-gray-300">
                  Access reseller tools and direct purchase
                </div>
              </button>

              <button
                onClick={() => navigate('/products')}
                className="card-astral p-4 hover:glow-blue transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">📦</span>
                  <span className="text-xs text-blue-400 group-hover:text-blue-300">
                    {stats.totalProducts} products
                  </span>
                </div>
                <div className="font-semibold text-white mb-1">Available Products</div>
                <div className="text-sm opacity-80 text-gray-300">
                  View products available for key generation
                </div>
              </button>

              <button
                onClick={() => navigate('/purchase')}
                className="card-astral p-4 hover:glow-yellow transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">🛒</span>
                  <span className="text-xs text-yellow-400 group-hover:text-yellow-300">
                    ${user?.balance || 0} balance
                  </span>
                </div>
                <div className="font-semibold text-white mb-1">Purchase Direct</div>
                <div className="text-sm opacity-80 text-gray-300">
                  Buy licenses directly with balance
                </div>
              </button>

              <button
                onClick={() => {
                  // Show reseller analytics
                  navigate('/admin');
                }}
                className="card-astral p-4 hover:glow-indigo transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl">📊</span>
                  <span className="text-xs text-indigo-400 group-hover:text-indigo-300">
                    Analytics
                  </span>
                </div>
                <div className="font-semibold text-white mb-1">My Statistics</div>
                <div className="text-sm opacity-80 text-gray-300">
                  View your sales and performance data
                </div>
              </button>
            </>
          )}
          
          {user?.role === 'owner' && (
            <>
              <button
                onClick={() => navigate('/manage-products')}
                className="btn-neon p-4 text-left"
              >
                <div className="text-lg font-semibold">Manage Products</div>
                <div className="text-sm opacity-80">Create & edit products</div>
              </button>
              <button
                onClick={() => navigate('/user-management')}
                className="btn-neon-blue p-4 text-left"
              >
                <div className="text-lg font-semibold">User Management</div>
                <div className="text-sm opacity-80">Manage users & roles</div>
              </button>
              <button
                onClick={() => navigate('/system-analytics')}
                className="btn-neon p-4 text-left"
              >
                <div className="text-lg font-semibold">System Analytics</div>
                <div className="text-sm opacity-80">View platform statistics</div>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
